// components/common/MetricCard.jsx - Compact Metric Card Component

import React from 'react';
import { createPortal } from 'react-dom';
import { InformationCircleIcon } from '@heroicons/react/24/outline';

/**
 * MetricCard Component
 * A versatile card component for displaying key metrics with optional trend indicators
 * 
 * @param {string} title - The metric title/label
 * @param {string} tooltip - Optional tooltip text explaining the metric
 * @param {string|number} value - The main metric value to display
 * @param {number} change - Percentage change from previous period
 * @param {string} changeLabel - Label describing the change period (e.g., "vs last month", "vs target (55%)")
 * @param {string} lastYearLabel - Year label for comparison (e.g., "2024")
 * @param {string|number} lastYearValue - The comparison value from last year
 * @param {React.Component} icon - Icon component to display (from heroicons)
 * @param {React.Component} actionIcon - Optional action icon to display (e.g., ChevronDownIcon)
 * @param {boolean} loading - Whether the card is in loading state
 * @param {function} onClick - Click handler function
 * @param {boolean} isClickable - Whether the card should be clickable
 * @param {string} inverseChangeColor - If true, negative change is good (green), positive is bad (red)
 * @param {string} subtitle - Optional subtitle text below the change indicator
 * @param {boolean} compareToTarget - If true, comparing to target instead of last year
 */

const MetricCard = ({ 
  title, 
  tooltip = null,
  value, 
  change = null,
  changeLabel = "vs prior period",
  lastYearLabel = null,
  lastYearValue = null,
  icon: Icon,
  actionIcon: ActionIcon = null,
  loading = false,
  onClick = null,
  isClickable = false,
  inverseChangeColor = false,
  subtitle = null,
  compareToTarget = false,
  periodLabel = null,
  periodType = null,
}) => {
  const [showTooltip, setShowTooltip] = React.useState(false);
  const [tooltipPosition, setTooltipPosition] = React.useState({ top: 0, left: 0 });
  const iconRef = React.useRef(null);

  const handleMouseEnter = () => {
    if (iconRef.current && tooltip) {
      const rect = iconRef.current.getBoundingClientRect();
      const tooltipWidth = window.innerWidth < 640 ? 192 : 224;
      const tooltipHeight = 80; // Approximate height
      
      // ✅ Position tooltip to the RIGHT of the icon
      const position = {
        top: rect.top + (rect.height / 2) - (tooltipHeight / 2), // Vertically center with icon
        left: rect.right + 12, // 12px to the right of the icon
      };
      
      // ✅ If tooltip would go off-screen on the right, position it to the LEFT instead
      if (position.left + tooltipWidth > window.innerWidth - 16) {
        position.left = rect.left - tooltipWidth - 12; // 12px to the left of icon
      }
      
      // ✅ Keep tooltip within viewport vertically
      if (position.top < 16) {
        position.top = 16;
      }
      if (position.top + tooltipHeight > window.innerHeight - 16) {
        position.top = window.innerHeight - tooltipHeight - 16;
      }
      
      setTooltipPosition(position);
      setShowTooltip(true);
    }
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };
  
  const getChangeColor = () => {
    if (change === 0 || change === null || change === undefined) {
      return 'text-gray-600';
    }
    
    const isPositive = change > 0;
    const isNegative = change < 0;
    
    // If inverse is true, negative is good (green), positive is bad (red)
    if (inverseChangeColor) {
      return isNegative ? 'text-green-600' : 'text-red-600';
    }
    
    // Normal: positive is good (green), negative is bad (red)
    return isPositive ? 'text-green-600' : 'text-red-600';
  };

  const getArrowIcon = () => {
    if (change === 0 || change === null || change === undefined) {
      return null;
    }
    
    const isPositive = change > 0;
    const isNegative = change < 0;
    
    // Determine color based on inverseChangeColor setting
    const colorClass = inverseChangeColor 
      ? (isNegative ? 'text-green-600' : 'text-red-600')
      : (isPositive ? 'text-green-600' : 'text-red-600');
    
    return (
      <span className={`inline-flex items-center justify-center ${colorClass} text-base sm:text-lg font-bold mr-1`}>
        {isPositive ? '⬆' : '⬇'}
      </span>
    );
  };

  // Loading state with compact sizing
  if (loading) {
    return (
      <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-gray-200 p-3 sm:p-4 lg:p-5 animate-pulse">
        <div className="flex items-start justify-between">
          <div className="h-10 w-10 sm:h-12 sm:w-12 bg-gray-200 rounded-xl flex-shrink-0"></div>
          <div className="text-right ml-3 flex-1 min-w-0">
            <div className="h-3 bg-gray-200 rounded w-16 sm:w-20 mb-1.5 ml-auto"></div>
            <div className="h-6 sm:h-7 bg-gray-200 rounded w-20 sm:w-24 ml-auto"></div>
          </div>
        </div>
        <div className="mt-2.5 sm:mt-3 pt-2.5 sm:pt-3 border-t border-gray-100">
          <div className="h-3 bg-gray-200 rounded w-28 sm:w-32"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div 
        className={`bg-white rounded-xl sm:rounded-2xl shadow-sm border border-gray-200 relative responsive-card-padding ${
          isClickable ? 'cursor-pointer hover:shadow-md transition-all duration-200' : ''
        }`}
        onClick={isClickable && onClick ? onClick : undefined}
      >
        {/* Period Badge - Top Right */}
        {periodLabel && (
          <div className="absolute top-2 right-2 sm:top-3 sm:right-3">
            <span className={`text-[10px] sm:text-xs font-semibold px-2 py-0.5 rounded-full`}>
              {periodLabel}
            </span>
          </div>
        )}

        {/* Header: Icon and Value */}
        <div className="flex items-start justify-between gap-2 sm:gap-3">
          {/* Icon Container - Compact sizing */}
          <div className="bg-teal-500 rounded-xl p-2 sm:p-2.5 lg:p-3 flex-shrink-0">
            {Icon && <Icon className="h-5 w-5 sm:h-6 sm:w-6 lg:h-7 lg:w-7 text-white" />}
          </div>
          
          {/* Value Section */}
          <div className="text-right flex-1 min-w-0">
            {/* Title Row with Tooltip */}
            <div className="flex items-center gap-1 mb-0.5">
              {/* Title - Compact text */}
              <div className="text-xs sm:text-sm font-medium text-gray-500 truncate">
                {title}
              </div>
              
              {/* Tooltip Icon */}
              {tooltip && (
                <div 
                  ref={iconRef}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                  onClick={(e) => e.stopPropagation()} // ✅ Prevent card click when hovering tooltip
                  className="flex-shrink-0"
                >
                  <InformationCircleIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400 hover:text-gray-600 cursor-help transition-colors" />
                </div>
              )}
            </div>

            {/* Main Value - Compact sizing */}
            <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 whitespace-nowrap leading-tight">
              {value}
            </p>
          </div>
        </div>
        
        {/* Change Indicator Section - Reduced spacing */}
        <div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-gray-100">
          {change !== null && change !== undefined ? (
            <div className="text-xs sm:text-sm flex items-center">
              {getArrowIcon()}
              <span className={`font-semibold ${getChangeColor()}`}>
                {Math.abs(change).toFixed(1)}%
              </span>
              <span className="text-gray-500 ml-1">
                {compareToTarget ? (
                  // When comparing to target, just show the changeLabel
                  changeLabel
                ) : (
                  // When comparing to last year, show the year and value
                  <>
                    vs {lastYearLabel || 'last year'} <span className="text-gray-400">({lastYearValue})</span>
                  </>
                )}
              </span>
            </div>
          ) : (
            <span className="text-xs sm:text-sm text-gray-500">{changeLabel}</span>
          )}
        </div>
        
        {/* Optional Subtitle */}
        {subtitle && (
          <div className="mt-1 text-xs text-gray-400">
            {subtitle}
          </div>
        )}
        
        {/* Click Hint with Action Icon - Reduced spacing */}
        {isClickable && (
          <div className="mt-1.5 sm:mt-2 text-xs text-gray-400 flex items-center justify-between gap-2">
            <span className="truncate flex-1">Click to view details</span>
            {ActionIcon && (
              <div className="group flex items-center justify-center h-5 w-5 flex-shrink-0 rounded-full border-2 border-gray-400 hover:border-primary-500 hover:bg-primary-500 hover:scale-110 transition-all cursor-pointer">
                <ActionIcon className="h-3 w-3 text-gray-400 group-hover:text-white transition-colors" />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Tooltip Portal - Rendered outside component hierarchy */}
      {showTooltip && tooltip && createPortal(
        <div 
          className="fixed w-48 sm:w-56 bg-gray-900 text-white text-xs sm:text-sm rounded-lg py-2 px-2.5 shadow-xl z-[9999] pointer-events-none"
          style={{
            top: `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`,
          }}
          ref={(el) => {
            if (el) console.log('✅ Tooltip element rendered:', el);
          }}
        >
          <div className="leading-relaxed">{tooltip}</div>
          
          {/* Arrow pointing up */}
          <div className="absolute bottom-full right-3 -mb-1">
            <div className="border-4 border-transparent border-b-gray-900"></div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default MetricCard;